import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Building2, CreditCard, MapPin, Eye, Copy, Check, ArrowUpRight, Lock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

// Contact data is split so we never expose raw text in initial HTML — the user has to click "Toon" first.
const CONTACTS = [
    {
        key: "name",
        icon: Building2,
        label: "Bedrijfsnaam",
        value: "VdS Gift Shop",
        revealLabel: "Toon bedrijfsnaam",
        testId: "contact-name",
    },
    {
        key: "email",
        icon: Mail,
        label: "E-mail",
        valueParts: ["vdsdesign2020", "gmail.com"],
        get value() { return this.valueParts.join("@"); },
        action: { href: "mailto:vdsdesign2020@gmail.com", label: "Open mail-app", external: false },
        revealLabel: "Toon e-mailadres",
        testId: "contact-email",
    },
    {
        key: "whatsapp",
        icon: MessageCircle,
        label: "WhatsApp",
        value: "+32 491 46 82 80",
        action: { href: "https://wa.me/32491468280", label: "Open WhatsApp", external: true },
        revealLabel: "Toon WhatsApp nummer",
        testId: "contact-whatsapp",
    },
    {
        key: "iban",
        icon: CreditCard,
        label: "IBAN",
        value: "BE16 0004 1120 9874",
        revealLabel: "Toon IBAN",
        testId: "contact-iban",
        requiresAuth: true,
    },
    {
        key: "address",
        icon: MapPin,
        label: "Retouradres",
        value: "Gentse baan 7, 9100 Sint-Niklaas",
        revealLabel: "Toon retouradres",
        testId: "contact-return-address",
    },
];

function ContactCard({ c, user }) {
    const [revealed, setRevealed] = useState(false);
    const [copied, setCopied] = useState(false);

    const isLocked = c.requiresAuth && !user;

    const value = typeof c.value === "string" ? c.value : c.value;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success(`${c.label} gekopieerd`);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Kopiëren niet gelukt");
        }
    };

    return (
        <div data-testid={c.testId} className="border-r border-b border-zinc-200 bg-white p-6 group transition-colors hover:bg-zinc-50/60">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center group-hover:border-zinc-900 transition-colors">
                    <c.icon size={18} strokeWidth={1.5} />
                </div>
                <p className="label-tiny">{c.label}</p>
                {c.requiresAuth && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">
                        <Lock size={9} strokeWidth={2.5} />
                        Account
                    </span>
                )}
            </div>

            {isLocked ? (
                <Link
                    to={`/login?next=${encodeURIComponent("/contact")}`}
                    data-testid={`${c.testId}-login-gate`}
                    className="mt-5 w-full group/btn inline-flex items-center justify-between gap-3 border border-amber-300 bg-amber-50 hover:border-amber-700 hover:bg-amber-700 hover:text-white text-amber-900 px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-all"
                >
                    <span className="inline-flex items-center gap-2">
                        <Lock size={14} strokeWidth={2} />
                        Log in om IBAN te zien
                    </span>
                    <span className="text-amber-500 group-hover/btn:text-white group-hover/btn:translate-x-0.5 transition-transform">→</span>
                </Link>
            ) : !revealed ? (
                <button
                    data-testid={`${c.testId}-reveal`}
                    onClick={() => setRevealed(true)}
                    className="mt-5 w-full group/btn inline-flex items-center justify-between gap-3 border border-zinc-300 bg-white hover:border-zinc-950 hover:bg-zinc-950 hover:text-white px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-all"
                >
                    <span className="inline-flex items-center gap-2">
                        <Eye size={14} strokeWidth={2} />
                        {c.revealLabel}
                    </span>
                    <span className="text-zinc-400 group-hover/btn:text-white group-hover/btn:translate-x-0.5 transition-transform">→</span>
                </button>
            ) : (
                <div className="mt-5 fade-in space-y-3">
                    <p className="font-display text-xl md:text-2xl break-all leading-tight">{value}</p>
                    <div className="flex flex-wrap gap-2">
                        {c.action && (
                            <a
                                data-testid={`${c.testId}-action`}
                                href={c.action.href}
                                target={c.action.external ? "_blank" : undefined}
                                rel={c.action.external ? "noopener noreferrer" : undefined}
                                className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors"
                            >
                                {c.action.label}
                                <ArrowUpRight size={12} strokeWidth={2.5} />
                            </a>
                        )}
                        <button
                            data-testid={`${c.testId}-copy`}
                            onClick={copy}
                            className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-950 px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors"
                        >
                            {copied ? <><Check size={12} strokeWidth={2.5} /> Gekopieerd</> : <><Copy size={12} strokeWidth={2.5} /> Kopiëren</>}
                        </button>
                        <button
                            onClick={() => setRevealed(false)}
                            className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-950 px-2 py-2 transition-colors"
                            data-testid={`${c.testId}-hide`}
                        >
                            Verbergen
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ContactForm({ user }) {
    const [form, setForm] = useState({
        name: user?.name || "",
        email: user?.email || "",
        subject: "",
        message: "",
        website: "",  // honeypot
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (form.message.trim().length < 10) {
            toast.error("Je bericht is te kort (min. 10 tekens).");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            setDone(true);
            toast.success("Bericht verstuurd — we reageren snel!");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Versturen niet gelukt — probeer later opnieuw");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div data-testid="contact-form-success" className="border border-emerald-200 bg-emerald-50 p-8 text-center">
                <div className="w-12 h-12 bg-emerald-600 text-white mx-auto flex items-center justify-center">
                    <Check size={22} strokeWidth={2.5} />
                </div>
                <p className="font-display text-3xl mt-4">Bericht ontvangen</p>
                <p className="text-sm text-emerald-900/80 mt-2 max-w-md mx-auto">
                    Bedankt! We hebben je bericht ontvangen en reageren meestal binnen 24 uur. Voor dringende vragen kan je ook WhatsApp gebruiken.
                </p>
                <button
                    onClick={() => { setDone(false); setForm({ ...form, subject: "", message: "" }); }}
                    className="btn-ghost mt-6"
                    data-testid="contact-form-new"
                >
                    Nieuw bericht sturen
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} data-testid="contact-form" className="border border-zinc-200 bg-white p-6 space-y-4">
            <div>
                <p className="label-tiny">Direct bericht sturen</p>
                <p className="font-display text-3xl mt-2">Liever via formulier?</p>
                <p className="text-sm text-zinc-500 mt-1">
                    Stuur ons een bericht zonder onze contactgegevens te tonen. We antwoorden binnen 24 uur.
                </p>
            </div>

            {/* Honeypot — verborgen voor mensen, bots vullen het in */}
            <div className="hidden" aria-hidden="true">
                <label>Website (laat leeg)
                    <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                    />
                </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="label-tiny block mb-2">Naam *</label>
                    <input
                        required
                        data-testid="contact-form-name"
                        type="text"
                        maxLength={80}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Jouw naam"
                        className="input-flat"
                    />
                </div>
                <div>
                    <label className="label-tiny block mb-2">E-mail *</label>
                    <input
                        required
                        data-testid="contact-form-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="naam@voorbeeld.be"
                        className="input-flat"
                    />
                </div>
            </div>

            <div>
                <label className="label-tiny block mb-2">Onderwerp *</label>
                <input
                    required
                    data-testid="contact-form-subject"
                    type="text"
                    maxLength={120}
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Vraag over bestelling / ontwerp / advies"
                    className="input-flat"
                />
            </div>

            <div>
                <label className="label-tiny block mb-2">Bericht * <span className="text-zinc-400 font-normal">({form.message.length}/2500)</span></label>
                <textarea
                    required
                    data-testid="contact-form-message"
                    rows={6}
                    maxLength={2500}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Hoe kunnen we je helpen?"
                    className="input-flat resize-y min-h-[140px]"
                />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-zinc-100">
                <p className="text-[10px] text-zinc-400">Je gegevens worden enkel gebruikt om je vraag te beantwoorden.</p>
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                >
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {submitting ? "Versturen…" : "Verstuur bericht"}
                </button>
            </div>
        </form>
    );
}

export default function ContactPage() {
    const { user } = useAuth();

    return (
        <div data-testid="contact-page" className="bg-zinc-50 min-h-screen">
            <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
                <p className="label-tiny">Contact</p>
                <h1 className="font-display text-5xl mt-2">Neem contact op.</h1>
                <p className="text-zinc-500 mt-3 max-w-xl">
                    Vragen over je ontwerp, een bestelling, of advies nodig? Stuur ons een bericht via het formulier hieronder,
                    of klik op een knop om de juiste gegevens te tonen.
                </p>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-zinc-200">
                    {CONTACTS.map((c) => (
                        <ContactCard key={c.key} c={c} user={user} />
                    ))}
                </div>

                <div className="mt-10">
                    <ContactForm user={user} />
                </div>

                <div className="mt-10 border border-zinc-200 bg-white p-6">
                    <p className="label-tiny">Snelste contact</p>
                    <p className="text-zinc-600 mt-2 text-sm leading-relaxed">
                        Stuur ons een WhatsApp bericht — meestal binnen het uur antwoord. Voor offertes of grote bestellingen kies je voor mail of het formulier.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <a
                            data-testid="contact-cta-whatsapp"
                            href="https://wa.me/32491468280"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe58] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
                        >
                            <MessageCircle size={14} /> WhatsApp ons
                        </a>
                        <a
                            data-testid="contact-cta-email"
                            href="mailto:vdsdesign2020@gmail.com"
                            className="btn-ghost inline-flex items-center gap-2"
                        >
                            <Mail size={14} /> Mail ons
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
